// Loading states: wireframes shaped like what is coming (a screen, a dialog, a table), a spinner, the boot screen.
// Skeletons fade in after a beat (CSS), so a fast load never flashes one.
import type { CSSProperties } from "react";
import { Logo } from "./Icon";

export const Sk = ({ w = "100%", h = 12, r, style }: { w?: number | string; h?: number; r?: number; style?: CSSProperties }) =>
  <span className="sk" style={{ width: w, height: h, borderRadius: r, ...style }} />;

export const Spinner = ({ label }: { label?: string }) => <span className="spin" role="status" aria-label={label ?? "Loading"} />;

const range = (n: number) => Array.from({ length: n }, (_, i) => i);
// deterministic "random" widths, so the wireframe looks like text without changing between renders
const w = (i: number, min: number, span: number) => `${min + ((i * 37) % span)}%`;

/** Rows of a list/table: an avatar or icon, a title and a sub line, a few right-aligned numbers. */
export function TableSkeleton({ rows = 8, cols = 3, avatar = true }: { rows?: number; cols?: number; avatar?: boolean }) {
  return (
    <div className="card sk-table">
      <div className="sk-row sk-head"><Sk w={90} h={10} />{range(cols).map((c) => <Sk key={c} w={60} h={10} style={{ marginLeft: "auto" }} />)}</div>
      {range(rows).map((i) => (
        <div className="sk-row" key={i}>
          {avatar && <Sk w={30} h={30} r={15} />}
          <span className="sk-lines"><Sk w={w(i, 30, 30)} h={12} /><Sk w={w(i + 3, 18, 25)} h={10} /></span>
          {range(cols).map((c) => <Sk key={c} w={48 + ((i + c) * 13) % 30} h={12} style={{ marginLeft: "auto" }} />)}
        </div>
      ))}
    </div>
  );
}

const Kpis = ({ n = 4 }: { n?: number }) => (
  <div className="kpis">{range(n).map((i) => <div className="kpi" key={i}><Sk w="55%" h={10} /><Sk w="45%" h={22} style={{ margin: "10px 0 8px" }} /><Sk w="70%" h={10} /></div>)}</div>
);
const Title = ({ action = true, back = false }: { action?: boolean; back?: boolean }) => (
  <div className="topbar">
    <div>{back && <Sk w={70} h={10} style={{ marginBottom: 8 }} />}<Sk w={200} h={22} /></div>
    <div className="grow" />{action && <Sk w={110} h={32} r={8} />}
  </div>
);
const Card = ({ lines = 4, h }: { lines?: number; h?: number }) => (
  <div className="card pad sk-card" style={{ minHeight: h }}><Sk w="35%" h={14} style={{ marginBottom: 14 }} />{range(lines).map((i) => <Sk key={i} w={w(i, 55, 40)} h={11} style={{ marginBottom: 10 }} />)}</div>
);

export type SkeletonKind = "dash" | "list" | "cards" | "detail" | "board";

/** The wireframe of a screen, while its first data loads. */
export function PageSkeleton({ kind }: { kind: SkeletonKind }) {
  return (
    <div className="skel" aria-busy="true" aria-label="Loading">
      {kind === "detail" ? <>
        <Title back />
        <Sk w="100%" h={40} r={8} style={{ marginBottom: 14 }} />
        <Kpis n={5} />
        <div className="grid g21"><Card lines={8} h={320} /><div className="stack"><Card lines={3} /><Card lines={3} /></div></div>
      </> : kind === "board" ? <>
        <Title />
        <Sk w="100%" h={52} r={10} style={{ marginBottom: 14 }} />
        <div className="kanban">{range(4).map((c) => (
          <div className="kcol" key={c}><Sk w="50%" h={12} style={{ marginBottom: 12 }} />{range(3 - (c % 2)).map((i) => <div className="kcard" key={i}><Sk w="70%" h={12} /><Sk w="50%" h={10} style={{ marginTop: 8 }} /></div>)}</div>
        ))}</div>
      </> : kind === "cards" ? <>
        <Title action={false} />
        {range(3).map((i) => <Card key={i} lines={3} />)}
      </> : <>
        <Title />
        {kind === "dash" ? <Kpis n={4} /> : <Sk w="100%" h={52} r={10} style={{ marginBottom: 14 }} />}
        <TableSkeleton rows={kind === "dash" ? 6 : 9} cols={kind === "dash" ? 4 : 3} />
      </>}
    </div>
  );
}

/** A dialog's wireframe: title, a few fields, the buttons. */
export function ModalSkeleton() {
  return (
    <div className="skel" aria-busy="true" aria-label="Loading">
      <Sk w="45%" h={18} style={{ marginBottom: 18 }} />
      {range(3).map((i) => <div key={i} style={{ marginBottom: 14 }}><Sk w={80} h={10} style={{ marginBottom: 7 }} /><Sk w="100%" h={36} r={8} /></div>)}
      <div className="modal-actions"><Sk w={80} h={34} r={8} /><Sk w={96} h={34} r={8} /></div>
    </div>
  );
}

/** Before we know who you are: the mark and a spinner, nothing to read. */
export const Boot = () => (
  <div className="boot" role="status" aria-label="Loading Compass"><div className="boot-mark"><Logo /></div><span className="spin" /></div>
);
